import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { useId } from 'react';
import { useTranslation } from "react-i18next";
import { FaInfoCircle } from "react-icons/fa";

const FileUploader = ({
  returnFilePath = false,
  fileNameInDB = "file",
  updateBackendEndpoint,
  generateUrlEndpoint,
  awsUploadDestination,
  maxSizeMB = 100,
  additionalFormData = {},
  allowedTypes = [],
  onSuccess,
  description = "",
  showPreview = null,   
}) => {
  const [fileObject, setFileObject] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [fileName, setFileName] = useState('');
  const [fileType, setFileType] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [showProgress, setShowProgress] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadedFile, setUploadedFile] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  
  const inputId = useId();
  const fileInputRef = useRef(null);

  const {t} = useTranslation('file-uploader')
  const translation = t('fileuploader')

  const labels = {
    preview: `${translation.preview}`,
    cancel: `${translation.cancel}`,
    successMessage: `${translation.success_msg}`,
    failedMessage: `${translation.failed_msg}`,
    loading: `${translation.loading}`,
    selectFile: `${translation.select_file}`,
    dragDrop: `${translation.drag_drop}`,
    or: `${translation.or }`,
    browse: `${translation.browse_file}`,
  }

  // Helper functions
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileIcon = () => {
    return (
      <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
      </svg>
    );
  };

  const getUploadIcon = () => {
    return (
      <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
      </svg>
    );
  };


  const getFileTypeIcon = () => {
    return (
      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    );
  };

  // Clear all states
  const resetStates = useCallback(() => {
    setFileObject(null);
    setFilePreview(null);
    setFileName('');
    setFileType('');
    setStatusMessage('');
    setErrorMessage('');
    setShowProgress(false);
    setUploadProgress(0);
    setUploadedFile(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  // File validation
  const validateFile = (file) => {
    // Validate file size
    if (file.size > maxSizeMB * 1024 * 1024) {
      setErrorMessage(`${translation.file_size_error} ${maxSizeMB}`);
      return false;
    }
    
    function isFileTypeAllowed(file) {
      const fileExtension = file.name.split('.').pop().toLowerCase();
      return allowedTypes.length === 0 || allowedTypes.includes(fileExtension);
    }

    // Validate file type
    if (!isFileTypeAllowed(file)) {
      setErrorMessage(`${translation.file_type_error} ${allowedTypes.join(',  ')}`);
      return false;
    }

    return true;
  };

  // Process file after validation
  const processFile = (file) => {
    setFileObject(file);
    setFileName(file.name);
    setFileType(file.type);
    setErrorMessage('');
    
    // Create preview
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setFilePreview(e.target.result);
      };
      reader.readAsDataURL(file);
    } else if (file.type.startsWith('video/')) {
      const url = URL.createObjectURL(file);
      setFilePreview(url);
    } else if (file.type === 'application/pdf') {
      const url = URL.createObjectURL(file);
      setFilePreview(url);
    } else {
      setFilePreview('document');  // fallback for unknown files
    }
  };

  // Cancel file selection
  const cancelFileSelection = () => {
    resetStates();
    setStatusMessage('File removed.');
  };

  // Handle file selection from input
  const handleFileSelection = (e) => {
    const file = e.target.files[0];
    
    if (!file) {
      setErrorMessage(`${translation.file_removed}`);
      return;
    }
    
    if (validateFile(file)) {
      processFile(file);
    }
  };

  // Drag and drop handlers
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      const file = files[0];
      if (validateFile(file)) {
        processFile(file);
      }
    }
  };

  // Open file selector
  const openFileSelector = () => {
    if (fileInputRef.current) {
      fileInputRef.current.removeAttribute('capture');
      fileInputRef.current.removeAttribute('accept');
      fileInputRef.current.click();
    }
  };



  useEffect(() => {
    // If an existing preview is passed and no new file is selected
    if (showPreview && !fileObject) {
      const extension = showPreview.split('.').pop().toLowerCase();
      let type = '';
      if (['mp4','mov','avi','mpeg'].includes(extension)) type = 'video';
      else if (['jpg','jpeg','png','gif','webp'].includes(extension)) type = 'image';
      else if (['pdf'].includes(extension)) type = 'pdf';
      else type = 'document';

      setFilePreview(showPreview);
      setFileName(showPreview.split('/').pop());
      setFileType(type);
    }

    // Cleanup blob URLs on unmount or when filePreview changes
    // return () => {
    //   if (filePreview && typeof filePreview === 'string' && filePreview.startsWith('blob:')) {
    //     URL.revokeObjectURL(filePreview);
    //   }
    // }; filePreview
  }, [showPreview, fileObject ]);

  // Upload completed handler
  const handleUploadCompleted = () => {
    setShowProgress(false);
    setStatusMessage(labels.successMessage);
    setIsSubmitting(false);
    setUploadedFile(true);

    // Disable upload button
    const uploadButton = document.querySelector('.upload-button');
    if (uploadButton) uploadButton.disabled = true;
  };

  // Handle file submission
  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    if (!fileObject) {
      setErrorMessage(`${translation.select_file_msg}`);
      return;
    }
    
    setIsSubmitting(true);
    setErrorMessage('');
    let filePath = '';
    
    try {
      // Get presigned URL
      const presignedResponse = await axios.get(
        generateUrlEndpoint,
        {
          params: { 
            file_name: fileName, 
            file_type: fileType, 
            destination: awsUploadDestination 
          },
          headers: {
            "Content-Type": "application/json",
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`
          },
        }
      );
      
      if (!presignedResponse.data?.presigned_url) {
        throw new Error(`${translation.pagination_error}`);
      }
      
      filePath = presignedResponse.data.path || fileName;
      const presignedUrl = presignedResponse.data.presigned_url;
      
      setStatusMessage(`${translation.uploading_msg}`);
      setShowProgress(true);

      // Upload to S3
      await axios.put(presignedUrl, fileObject, {
        headers: { "Content-Type": fileType },
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total) {
            const percent = Math.round((progressEvent.loaded / progressEvent.total) * 100);
            setUploadProgress(percent);
          }
        },
      });

      // If returnFilePath is true, skip the POST request and call onSuccess
      if (returnFilePath) {
        if (onSuccess) {
          onSuccess(filePath); // Pass filePath to parent
        }
      } else {
        // Otherwise, proceed with POST to update backend
        if (updateBackendEndpoint) {
          setStatusMessage(`${translation.finalizing_file}`);
          const formData = new FormData();
          formData.append(fileNameInDB, filePath);
          
          if (additionalFormData) {
            Object.keys(additionalFormData).forEach(key => {
              formData.append(key, additionalFormData[key]);
            });
          }
          
          await axios.post(updateBackendEndpoint, formData, {
            headers: {
              "Content-Type": "multipart/form-data",
              'Authorization': `Bearer ${localStorage.getItem('access_token')}`
            },
          });
        }
      }

      handleUploadCompleted();

      // tell caller that file has been uploaded
      if (!returnFilePath && onSuccess){
        onSuccess(filePath)
      }
      
    } catch (err) {
      setIsSubmitting(false);
      setShowProgress(false);
      
      let errorMsg = `${translation.someting_went_wrong}`;
      
      if (err.response) {
        // Handle API endpoint errors
        if (err.response.status === 401) {
          errorMsg = `${translation.first_login}`;
        } 
        else if (err.response.status >= 400 && err.response.status < 500) {
          errorMsg = err.response.data?.error || `${translation.data_error}`;
        } 
        else if (err.response.status >= 500) {
          errorMsg = `${translation.server_error}`;
        }
      } else if (err.request) {
        errorMsg = `${translation.network_error}`;
      } else if (err.message.includes("presigned URL")) {
        errorMsg = `${translation.pagination_error}`;
      }

      setErrorMessage(errorMsg);
    }
  };

  return (
    <div className="file-uploader w-full max-w-2xl mx-auto">
      <div className="space-y-4">

        {description && (
          <div className="flex items-start gap-x-2 text-dark-1 dark:text-white bg-gray-100 dark:bg-gray-800 p-3 rounded-md">
            <FaInfoCircle className="mt-1 text-dark-1 dark:text-white flex-shrink-0" />
            <div className="text-sm leading-relaxed">
              {description}
            </div>
          </div>
        )}
        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          id={inputId}
          className="hidden"
          disabled={isSubmitting}
          onChange={handleFileSelection}
        />

        {/* Main upload area */}
        {!filePreview ? (
          <div
            className={`relative border-2 border-dashed rounded-lg p-6 transition-colors duration-200 ${
              isDragOver 
                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' 
                : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
            }`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <div className="text-center">
              
              <div className="mx-auto text-gray-400 mb-4 w-fit">
                {getUploadIcon()}
              </div>
              
              {/* Desktop drag & drop text */}
              <div className="hidden sm:block">
                <p className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
                  {labels.dragDrop}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                  {labels.or}
                </p>
              </div>

              {/* Mobile-friendly buttons */}
              <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
                <button
                  type="button"
                  onClick={openFileSelector}
                  className="w-full sm:w-auto flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-6 rounded-lg transition-colors duration-200 touch-manipulation"
                  disabled={isSubmitting}
                >
                  {getFileTypeIcon()}
                  {labels.browse}
                </button>
              </div>

              {/* File format info */}
              <div className="mt-4 text-xs text-gray-500 dark:text-gray-400 space-y-1">
                <p>Max file size: {maxSizeMB}MB</p>
                {allowedTypes.length > 0 && (
                  <p>Supported: {allowedTypes.join(', ')}</p>
                )}
              </div>
            </div>
          </div>
        ) : (
          /* File preview section */
          <div className="mt-4 p-4 border rounded-lg bg-gray-50 dark:bg-gray-800">
            <div className="flex justify-between items-center mb-3">
              <h4 className="text-sm text-black dark:text-white font-bold">
                {labels.preview}
              </h4>
              <button
                onClick={cancelFileSelection}
                className="p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-full transition-colors touch-manipulation"
                disabled={isSubmitting}
              >
                <svg className="h-4 w-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* File preview content */}
            <div className="space-y-3">
              {filePreview === 'document' ? (
                <div className="flex items-center space-x-3 p-3 bg-white dark:bg-gray-700 rounded border">
                  <div className="text-gray-500 dark:text-gray-400">
                    {getFileIcon()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                      {fileName}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {formatFileSize(fileObject?.size || 0)}
                    </p>
                  </div>
                </div>
              ) : fileObject?.type?.startsWith('video/') ? (
                <video 
                  controls 
                  className="w-full max-h-64 rounded-lg"
                  src={filePreview}
                  onLoadedData={() => URL.revokeObjectURL(filePreview)}
                />
              ) : fileObject?.type === 'application/pdf' ? (
                <div className="bg-white dark:bg-gray-700 rounded-lg p-4">
                  <iframe
                    src={filePreview}
                    className="w-full h-64 border rounded"
                    title={translation.pdf_preview}
                  />
                </div>
              ) : fileType?.startsWith('pdf') ? (
                <div className="bg-white dark:bg-gray-700 rounded-lg p-4">
                  <iframe
                    src={filePreview}
                    className="w-full h-64 border rounded"
                    title={translation.pdf_preview}
                  />
                </div>
              ) : fileType?.startsWith('video') ? (
                  <video controls className="w-full max-h-64 rounded-lg" src={filePreview} />
                ) : (
                <img 
                  src={filePreview} 
                  alt={translation.pdf_preview || 'File preview'} 
                  className="w-full max-h-64 object-contain rounded-lg bg-white dark:bg-gray-700"
                />
              )}

              {/* File info */}
              <div className="text-xs text-gray-600 dark:text-gray-400">
                <span className="font-medium">{fileName}</span>
                <span className="ml-2">({formatFileSize(fileObject?.size || 0)})</span>
              </div>
            </div>
          </div>
        )}

        {/* Progress bar */}
        {showProgress && (
          <div className="space-y-2">
            <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden dark:bg-gray-700">
              <div
                className="h-full bg-blue-500 transition-all duration-300 ease-out"
                style={{ width: `${uploadProgress}%` }}
              />
            </div>
            <div className="flex justify-between text-xs text-gray-600 dark:text-gray-400">
              <span>{labels.loading}</span>
              <span>{uploadProgress}%</span>
            </div>
          </div>
        )}

        {/* Action buttons */}
        {filePreview && !isSubmitting && (
          <div className="flex flex-col sm:flex-row gap-3">
            <button 
              type="button" 
              onClick={cancelFileSelection}
              disabled={isSubmitting || uploadedFile}
              className="flex-1 py-3 px-4 bg-gray-500 hover:bg-gray-600 text-white font-medium rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 touch-manipulation"
            >
              {labels.cancel}
            </button>
            <button 
              type="button"
              onClick={handleSubmit}
              disabled={isSubmitting || !fileObject || uploadedFile}
              className="upload-button flex-1 py-3 px-4 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 touch-manipulation"
            >
              {translation.submit}
            </button>
          </div>
        )}

        {/* Loading indicator */}
        {isSubmitting && !showProgress && (
          <div className="flex items-center justify-center py-4">
            <div className="animate-spin h-6 w-6 border-2 border-blue-500 rounded-full border-t-transparent"></div>
            <span className="ml-3 text-sm text-gray-600 dark:text-gray-400">{labels.loading}</span>
          </div>
        )}

        {/* Error message */}
        {errorMessage && (
          <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <p className="az-error-message">{errorMessage}</p>
          </div>
        )}

        {/* Success message */}
        {statusMessage && !errorMessage && (
          <div className="p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
            <p className="text-sm text-green-700 dark:text-green-400 font-medium">{statusMessage}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default FileUploader;